<script setup>
import { computed } from 'vue';
import { socketState } from '../api/socket.js';
import { queueState, resolveBlocked } from '../api/queue.js';
import { useI18n } from '../i18n/index.js';

/* Полоса состояния связи и очереди.

   В цехе это не украшение: оператор должен видеть, ушли его нажатия
   на сервер или лежат в очереди. Молчаливая потеря учёта — худшее,
   что может случиться со сменой. */

const pending = computed(() => queueState.items.length);

const state = computed(() => {
  if (queueState.blocked) return 'blocked';
  if (!queueState.online) return 'offline';
  if (pending.value) return 'pending';
  if (!socketState.connected) return 'degraded';
  return 'ok';
});

/* Полоса висит у оператора перед глазами всю смену, и по-русски она
   оставалась даже на узбекском экране. Утечка была не в редком диалоге,
   а в том, что читают чаще всего. */
const { t } = useI18n();

const view = computed(() => ({
  ok: { c: 'var(--st-run)', text: t('status.ok') },
  pending: { c: 'var(--c-warn)', text: t('status.pending', { n: pending.value }) },
  offline: {
    c: 'var(--c-bad)',
    text: pending.value
      ? t('status.offlineQueued', { n: pending.value })
      : t('status.offline'),
  },
  degraded: { c: 'var(--c-warn)', text: t('status.degraded') },
  /* Текст ошибки очереди приходит от сервера и уже на языке ответа —
     переводим только запасную подпись. */
  blocked: { c: 'var(--c-bad)', text: queueState.lastError || t('status.blocked') },
}[state.value]));
</script>

<template>
  <div class="strip" :class="{ quiet: state === 'ok', loud: state === 'blocked' }" :style="{ '--c': view.c }">
    <span class="dot" :class="{ pulse: state === 'pending' }" />
    <span class="txt">{{ view.text }}</span>

    <span v-if="state === 'pending' && queueState.sending" class="spin" />

    <!-- Очередь встала: решение принимает человек, молча отбрасывать нельзя -->
    <template v-if="state === 'blocked'">
      <span class="spacer" />
      <button class="btn btn-ghost small-btn" @click="resolveBlocked({ drop: false })">{{ t('status.retry') }}</button>
      <button class="btn btn-ghost small-btn danger" @click="resolveBlocked({ drop: true })">{{ t('status.discard') }}</button>
    </template>
  </div>
</template>

<style scoped>
/* Пилюля, а не полоса во всю ширину. Состояние связи должно быть видно
   всегда, но «связь есть» — это норма, и кричать о норме незачем: в спокойном
   состоянии остаётся тихая серая пилюля с зелёной точкой, а цвет и заметность
   появляются ровно тогда, когда что-то пошло не так. */
.strip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 12px 4px 10px; min-height: 30px;
  font-size: var(--fs-label); font-weight: 500;
  border-radius: var(--r-pill);
  background: color-mix(in srgb, var(--c) calc(var(--tint-alpha) * 100%), var(--tint-base));
  color: var(--c);
  border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
}
.strip.quiet {
  background: var(--surface);
  border-color: var(--border);
  color: var(--t3);
}

/* Остановленная очередь разворачивается во всю ширину.

   Тихая пилюля в углу годится для «связь есть», но не для отказа: оператор
   жмёт кнопку выпуска внизу экрана, счётчик обнуляется, и если отказ показать
   мелким значком в углу, человек уйдёт уверенным, что детали посчитаны.
   Это худшее, что может случиться со сменой. */
.strip.loud {
  flex: 1 0 100%;
  border-radius: var(--r-md);
  padding: 10px 14px;
  font-size: var(--fs-body);
  font-weight: 600;
}
.txt { color: inherit; }
.pulse { animation: pulse 1.4s ease-in-out infinite; }
@keyframes pulse { 50% { opacity: .35; } }
/* 32 вместо 26: полоса состояния висит на рабочем месте оператора. */
.small-btn { min-height: 32px; padding: 0 10px; font-size: 12px; color: var(--c); }

/* В остановленной очереди кнопки — не мелкие.

   26 пикселей годятся для тихой пилюли в углу. Но в красной полосе стоит
   выбор, который решает судьбу уже сделанной работы: «Повторить» отправит
   заново, «Отбросить» выбросит запись о выпуске насовсем. Это происходит
   у станка, в перчатке, и промах здесь стоит смены. Вокруг всё по 44 —
   а самые ответственные две кнопки были самыми маленькими на экране.

   Полоса громкая ровно тогда, когда очередь стоит, поэтому правило висит
   на ней, а не на кнопке: в спокойном состоянии пилюля остаётся тихой. */
.strip.loud .small-btn {
  min-height: 40px; padding: 0 16px; font-size: var(--fs-body); font-weight: 600;
  border: 1px solid color-mix(in srgb, var(--c) 40%, transparent);
}
.danger { color: var(--c-bad); }
</style>
